import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, CheckCircle2 } from "lucide-react";
import { api, type ApiCategory, type ApiVideo } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function VideosPage() {
  const { user } = useAuth();
  const [videos, setVideos] = useState<ApiVideo[]>([]);
  const [cats, setCats] = useState<ApiCategory[]>([]);
  const [done, setDone] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState<number | "all">("all");

  useEffect(() => {
    Promise.all([api.listVideos(), api.listCategories(), api.myCheckins()])
      .then(([v, c, k]) => {
        setVideos(v.videos);
        setCats(c.categories);
        setDone(new Set(k.completedVideoIds));
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (activeCat === "all" ? videos : videos.filter((v) => v.category_id === activeCat)),
    [videos, activeCat]
  );

  const grouped = useMemo(() => {
    const map = new Map<number, ApiVideo[]>();
    filtered.forEach((v) => {
      const arr = map.get(v.category_id) || [];
      arr.push(v);
      map.set(v.category_id, arr);
    });
    return cats
      .map((c) => ({ cat: c, vids: map.get(c.id) || [] }))
      .filter((g) => g.vids.length > 0);
  }, [filtered, cats]);

  return (
    <main className="container-narrow py-10 lg:py-14">
      {/* Hero */}
      <div className="mb-8 flex flex-col gap-4 border-b border-surface-border pb-8 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="badge mb-3">内部学习平台</div>
          <h1 className="font-display text-3xl font-semibold leading-tight text-ink-900 md:text-4xl">
            欢迎回来，{user?.displayName || user?.username}
          </h1>
          <p className="mt-2 max-w-xl text-sm text-ink-400">
            观看 Mediversity 为您专属准备的医学英语课程视频。请勿外传。
          </p>
        </div>
        <div className="flex gap-6 text-sm text-ink-400">
          <div>
            <div className="font-display text-2xl font-semibold text-ink-900">{videos.length}</div>
            <div className="text-xs">个视频</div>
          </div>
          <div>
            <div className="font-display text-2xl font-semibold text-ink-900">{cats.length}</div>
            <div className="text-xs">个分类</div>
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="mb-8 flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCat("all")}
          className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
            activeCat === "all"
              ? "bg-brand text-white"
              : "border border-surface-border bg-white text-ink-600 hover:bg-surface-muted"
          }`}
        >
          全部
        </button>
        {cats.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCat(c.id)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              activeCat === c.id
                ? "bg-brand text-white"
                : "border border-surface-border bg-white text-ink-600 hover:bg-surface-muted"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="py-20 text-center text-sm text-ink-400">加载中…</div>
      ) : videos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-surface-border bg-white py-20 text-center">
          <PlayCircle className="mx-auto h-12 w-12 text-ink-400/50" />
          <p className="mt-4 text-sm text-ink-400">还没有视频。管理员可在 /admin 添加。</p>
        </div>
      ) : (
        <div className="space-y-12">
          {grouped.map(({ cat, vids }) => (
            <section key={cat.id}>
              <div className="mb-5 flex items-baseline justify-between">
                <h2 className="font-display text-xl font-semibold text-ink-900">{cat.name}</h2>
                <span className="text-xs text-ink-400">{vids.length} 个视频</span>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {vids.map((v) => (
                  <VideoCard key={v.id} v={v} done={done.has(v.id)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </main>
  );
}

function VideoCard({ v, done }: { v: ApiVideo; done: boolean }) {
  let progress: { t: number; d: number } | null = null;
  try {
    const raw = localStorage.getItem(`mvg_video_progress:${v.id}`);
    if (raw) {
      const j = JSON.parse(raw);
      if (typeof j.t === "number" && typeof j.d === "number" && j.d > 0) {
        progress = j;
      }
    }
  } catch {}
  const pct = progress ? Math.min(100, Math.round((progress.t / progress.d) * 100)) : 0;

  return (
    <Link to={`/watch/${v.id}`} className="card group flex flex-col overflow-hidden">
      <div className="relative aspect-video w-full overflow-hidden bg-ink-900">
        <img
          src={v.cover_path}
          alt={v.title}
          className="h-full w-full object-cover transition group-hover:scale-[1.02]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/40 to-transparent" />
        <div className="absolute right-3 top-3 inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-brand">
          {v.category_name}
        </div>
        <PlayCircle className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-white/80 opacity-0 transition group-hover:opacity-100" />
        {done && (
          <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-semibold text-white">
            <CheckCircle2 className="h-3.5 w-3.5" /> 已完成
          </div>
        )}
        {progress && pct > 0 && (
          <>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
              <div className="h-full bg-brand-light" style={{ width: `${pct}%` }} />
            </div>
            <div className="absolute bottom-2 left-3 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">
              已观看 {pct}%
            </div>
          </>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-base font-semibold leading-snug text-ink-900 group-hover:text-brand">
          {v.title}
        </h3>
        {v.description && (
          <p className="mt-1 text-sm text-ink-400 line-clamp-2">{v.description}</p>
        )}
      </div>
    </Link>
  );
}
